"use client";

import { c as _c } from "react/compiler-runtime";
import { resolveMegaMenuLogoDisplayHeightPx, resolveMegaMenuLogoRasterMaxPx } from "@multi-tenant-cms/builder/ui/lib/megaMenuHeaderLogo";
import type { MegaMenuHeaderRuntimePayload } from "@multi-tenant-cms/types/menu";
import { type MouseEvent as ReactMouseEvent, type ReactNode, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { MegaMenuBuilderPanel } from "./MegaMenuPanel";
import { imgElementIntrinsicProps } from "./mediaResponsive";
import styles from "./mega-menu-header.module.css";
import { applyMegaMenuDesignCssVars } from "./megaMenuDesignCssVars";
import { isLocaleCurrentForPath, resolveLocaleCodeFromPath } from "./megaMenuLocale";
import { consumeOpenMegaTabLabel, findMegaTabByLabel } from "./megaMenuOpenTabRestore";
import { useMegaMenuStickyHeader } from "./useMegaMenuStickyHeader";

/** Avoid using image `src` or media API paths as the logo link target. */
function safeLogoHref(logoHref: string | undefined, logoUrl?: string): string {
  const href = (logoHref ?? "/").trim() || "/";
  const url = logoUrl?.trim();
  if (url && href === url) {
    return "/";
  }
  if (/\/api\/media\//i.test(href)) {
    return "/";
  }
  if (/^https?:\/\/\S+\.(png|jpe?g|gif|webp|svg|avif)(\?|#|$)/i.test(href)) {
    return "/";
  }
  return href;
}
type MegaTabRoot = Extract<MegaMenuHeaderRuntimePayload["roots"][number], {
  itemKind: "mega_tab";
}>;
type ViewportMode = "mobile" | "tablet" | "desktop";
const MOBILE_MAX = 767;
const TABLET_MAX = 1023;
type MenuRoot = MegaMenuHeaderRuntimePayload["roots"][number];
function menuRootKey(root: MenuRoot, index: number): string {
  if (root.id) {
    return root.id;
  }
  return `root-${root.itemKind}-${root.sortOrder}-${index}`;
}

/** String literals satisfy static a11y analyzers (e.g. Edge Tools axe) that cannot evaluate JSX booleans. */
const ARIA_EXPANDED_TRUE = {
  "aria-expanded": "true"
} as const;
const ARIA_EXPANDED_FALSE = {
  "aria-expanded": "false"
} as const;
const ARIA_HIDDEN_TRUE = {
  "aria-hidden": "true"
} as const;
const ARIA_HIDDEN_FALSE = {
  "aria-hidden": "false"
} as const;

/** Mega tab roots that open a flyout panel (groups, builder content, promo, and/or footer pills). */
function megaTabHasPanelContent(root: MegaTabRoot, promos: MegaMenuHeaderRuntimePayload["promos"], footerRows?: MegaMenuHeaderRuntimePayload["footerRows"]): boolean {
  if (root.megaMenuContent?.root) {
    return true;
  }
  if (root.groups.some(group => group.links.length > 0)) {
    return true;
  }
  const promo = promos[root.id];
  if (promo != null && promo.enabled !== false) {
    return true;
  }
  const footer = footerRows?.[root.id];
  return footer != null && footer.enabled !== false && footer.links.some(l => l.label.trim().length > 0);
}
function resolveColumnCount(root: MegaTabRoot): 2 | 3 | 4 {
  if (root.columnCount === 2 || root.columnCount === 3 || root.columnCount === 4) {
    return root.columnCount;
  }
  let maxIndex = 0;
  for (const g of root.groups) {
    if (g.columnIndex > maxIndex) {
      maxIndex = g.columnIndex;
    }
  }
  const derived = maxIndex + 1;
  if (derived <= 2) {
    return 2;
  }
  if (derived >= 4) {
    return 4;
  }
  return 3;
}
function groupsByColumn(groups: MegaTabRoot["groups"], columnCount: 2 | 3 | 4): Array<MegaTabRoot["groups"]> {
  const cols: Array<MegaTabRoot["groups"]> = Array.from({
    length: columnCount
  }, () => []);
  for (const g of groups) {
    const i = Math.min(columnCount - 1, Math.max(0, g.columnIndex));
    cols[i].push(g);
  }
  return cols;
}
function useViewportMode(forced) {
  const $ = _c(3);
  const [mode, setMode] = useState("desktop");
  let t0;
  let t1;
  if ($[0] !== forced) {
    t0 = () => {
      if (forced === "mobile" || forced === "tablet" || forced === "desktop") {
        return;
      }
      const update = () => {
        const w = window.innerWidth;
        if (w <= MOBILE_MAX) {
          setMode("mobile");
        } else {
          if (w <= TABLET_MAX) {
            setMode("tablet");
          } else {
            setMode("desktop");
          }
        }
      };
      update();
      window.addEventListener("resize", update);
      return () => window.removeEventListener("resize", update);
    };
    t1 = [forced];
    $[0] = forced;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  if (forced === "mobile" || forced === "tablet" || forced === "desktop") {
    return forced;
  }
  return mode;
}
function setPromoGradient(el: HTMLElement | null, gradient: string | undefined) {
  if (!el) {
    return;
  }
  if (gradient) {
    el.style.setProperty("--mmh-promo-gradient", gradient);
  } else {
    el.style.removeProperty("--mmh-promo-gradient");
  }
}
function resolveColumnMaxWidthPx(value: number | undefined): number {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return 240;
  }
  return Math.min(480, Math.max(120, Math.round(value)));
}

/**
 * Compact capsule trigger text: prefer `UK · GBP`-style pairs from the label;
 * otherwise the short locale `code` (e.g. `UK`). Does not invent a currency.
 */
function formatLocaleCapsuleLabel(locale: {
  code: string;
  label: string;
}): string {
  const label = locale.label.trim();
  const pair = label.match(/^([A-Za-z]{2,4})\s*[-–—·/]\s*([A-Za-z]{2,4})$/);
  if (pair?.[1] && pair[2]) {
    return `${pair[1].toUpperCase()} · ${pair[2].toUpperCase()}`;
  }
  const code = locale.code.trim().toUpperCase();
  if (code.length > 0 && code.length <= 5) {
    return code;
  }
  const paren = label.match(/\(([A-Za-z]{2,5})\)\s*$/);
  if (paren?.[1]) {
    return paren[1].toUpperCase();
  }
  return label || code || "—";
}
function LangGlobeIcon() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg aria-hidden={true} className={styles.langGlobe} fill="none" focusable="false" height="16" viewBox="0 0 24 24" width="16" xmlns="http://www.w3.org/2000/svg"><title>Globe</title><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5" /><ellipse cx="12" cy="12" rx="4" ry="9" stroke="currentColor" strokeWidth="1.5" /><path d="M3.5 12h17M5.2 7.5h13.6M5.2 16.5h13.6" stroke="currentColor" strokeLinecap="round" strokeWidth="1.35" /></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}

/** Returns clamped px, or undefined when unset/0 (no content-width constraint). */
function resolvePanelContentMaxWidthPx(value: number | undefined): number | undefined {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return undefined;
  }
  const rounded = Math.round(value);
  if (rounded <= 0) {
    return undefined;
  }
  return Math.min(1920, Math.max(480, rounded));
}
function MegaPanelColumns({
  root,
  promo,
  footerRow,
  columnClassName,
  gridClassName,
  centerColumns = true,
  columnMaxWidthPx = 240
}: {
  root: MegaTabRoot;
  promo: MegaMenuHeaderRuntimePayload["promos"][string] | undefined;
  footerRow?: MegaMenuHeaderRuntimePayload["footerRows"][string];
  columnClassName?: string;
  gridClassName?: string;
  centerColumns?: boolean;
  columnMaxWidthPx?: number;
}) {
  if (root.megaMenuContent?.root) {
    return <MegaMenuBuilderPanel columnClassName={columnClassName} gridClassName={gridClassName} root={root} />;
  }
  const columnCount = resolveColumnCount(root);
  const promoHtmlMode = promo?.contentMode === "html";
  const promoHtml = promoHtmlMode ? promo?.html?.trim() ?? "" : "";
  const showPromo = promo != null && promo.enabled !== false && (promoHtmlMode ? promoHtml.length > 0 : true);
  const footerLinks = footerRow != null && footerRow.enabled !== false ? footerRow.links.filter(l => l.label.trim().length > 0) : [];
  const showFooter = footerLinks.length > 0;
  const maxWidth = resolveColumnMaxWidthPx(columnMaxWidthPx);
  const centered = centerColumns !== false;
  const promoTrack = Math.round(maxWidth * 1.3);
  const isDrawer = gridClassName != null;
  const gridStyle = gridClassName == null ? {
    ...(centered ? {
      gridTemplateColumns: showPromo ? `repeat(${columnCount}, minmax(0, ${maxWidth}px)) ${promoTrack}px` : `repeat(${columnCount}, minmax(0, ${maxWidth}px))`,
      justifyContent: "center" as const
    } : {
      gridTemplateColumns: showPromo ? `repeat(${columnCount}, minmax(0, 1fr)) minmax(${promoTrack}px, 1.3fr)` : `repeat(${columnCount}, minmax(0, 1fr))`
    }),
    ["--mmh-col-max-width" as string]: `${maxWidth}px`
  } : undefined;
  const columns = <div className={gridClassName ?? `${styles.megaInner}${centered ? "" : ` ${styles.megaInnerStretch}`}`} data-mmh-cols={columnCount} style={gridStyle}>
			{groupsByColumn(root.groups, columnCount).map((colGroups, colIndex) => <div className={columnClassName ?? styles.col} key={`${root.id}-col-${colIndex}`}>
					{colGroups.map((group, groupIndex) => {
        const groupHref = group.href?.trim();
        const linked = Boolean(groupHref) && groupHref !== "#" && groupHref !== "/#";
        return <div className={styles.group} key={group.id || `${root.id}-group-${groupIndex}`}>
								{linked && groupHref ? <a className={styles.groupTitle} href={groupHref}>
										{group.label}
									</a> : <p className={styles.groupTitle}>{group.label}</p>}
								<ul className={styles.linkList}>
									{group.links.map((link, linkIndex) => {
              const description = link.description?.trim();
              return <li className={styles.linkItem} key={link.id || `${group.id || groupIndex}-link-${linkIndex}`}>
												<a className={styles.link} href={link.href} rel={link.openInNewTab ? "noopener noreferrer" : undefined} target={link.openInNewTab ? "_blank" : undefined}>
													<span className={styles.linkBody}>
														<span className={styles.linkText}>
															{link.label}
														</span>
														{description ? <span className={styles.linkDescription}>
																{description}
															</span> : null}
													</span>
													<span aria-hidden className={styles.linkArrow}>
														→
													</span>
												</a>
											</li>;
            })}
								</ul>
							</div>;
      })}
				</div>)}
			{showPromo && promo ? promoHtmlMode ? <div className={`${styles.promo} ${styles.promoHtml} ${columnClassName ?? ""}`}
    // Editor-authored featured HTML (same trust model as FAQ answerHtml / custom_code).
    dangerouslySetInnerHTML={{
      __html: promoHtml
    }} /> : <a className={`${styles.promo} ${columnClassName ?? ""}`} href={promo.href || "#"} ref={el => setPromoGradient(el, promo.gradient)}>
						<div aria-hidden className={styles.promoBg} />
						<span className={styles.promoTag}>{promo.tag}</span>
						<div>
							<p className={styles.promoTitle}>{promo.title}</p>
							<div className={styles.promoCta}>
								<span>Learn more</span>
								<span aria-hidden>→</span>
							</div>
						</div>
					</a> : null}
		</div>;
  const footer = showFooter ? <div className={isDrawer ? styles.drawerFooterRow : styles.megaFooterRow}>
			{footerRow?.label.trim() ? <p className={styles.megaFooterLabel}>{footerRow.label}</p> : null}
			<ul className={styles.megaFooterPills}>
				{footerLinks.map(link_0 => <li key={link_0.id || `${link_0.label}-${link_0.href}`}>
						<a className={styles.megaFooterPill} href={link_0.href || "#"}>
							{link_0.label}
						</a>
					</li>)}
			</ul>
		</div> : null;
  if (isDrawer) {
    return <div className={styles.drawerMegaStack}>
				{columns}
				{footer}
			</div>;
  }
  return <div className={styles.megaPanelContent}>
			{columns}
			{footer}
		</div>;
}
export function MegaMenuHeader({
  payload,
  afterLogo,
  drawerLead,
  previewMode = false,
  currentPathname = null,
  onActiveMegaTabChange,
  forcedViewport = null
}: {
  payload: MegaMenuHeaderRuntimePayload;
  /** Optional chrome between logo and primary nav (e.g. audience toggle). */
  afterLogo?: ReactNode;
  /** Optional block under the mobile drawer title (e.g. audience toggle). */
  drawerLead?: ReactNode;
  /** Builder canvas: block navigation so header links do not leave the editor. */
  previewMode?: boolean;
  /** Public request pathname — SSR-correct language when URL matches a locale. */
  currentPathname?: string | null;
  /**
   * Fired when a mega panel tab is open/closed (label stable across audience menus).
   * Used to reinstate the panel after Individuals ↔ Businesses navigation.
   */
  onActiveMegaTabChange?: (tab: {
    id: string;
    label: string;
  } | null) => void;
  /**
   * Builder device toolbar: force mobile/tablet chrome instead of `window.innerWidth`.
   */
  forcedViewport?: ViewportMode | null;
}) {
  const viewport = useViewportMode(forcedViewport);
  const isCompact = viewport !== "desktop";
  const [activeTab, setActiveTab] = useState<string | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [expandedMegaTabId, setExpandedMegaTabId] = useState<string | null>(null);
  // Tracks every drawer tab opened at least once — a never-opened tab's panel
  // (including its promo custom-code) is not rendered at all, so it can't be
  // serialized into the initial RSC payload. Only grows; a collapsed-again tab
  // stays mounted so the collapse animation still plays.
  const [openedMegaTabIds, setOpenedMegaTabIds] = useState<Set<string>>(() => new Set());
  const [langOpen, setLangOpen] = useState(false);
  const pathLocaleCode = useMemo(() => resolveLocaleCodeFromPath(payload.languageSwitcher, currentPathname), [payload.languageSwitcher, currentPathname]);
  const [langCode, setLangCode] = useState(pathLocaleCode);
  useEffect(() => {
    setLangCode(pathLocaleCode);
  }, [pathLocaleCode]);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const langCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const navRef = useRef<HTMLElement>(null);
  const pillRef = useRef<HTMLSpanElement>(null);
  const drawerRef = useRef<HTMLElement>(null);
  const menuToggleRef = useRef<HTMLButtonElement>(null);
  /** Label of the open mega tab — rematch when audience menuId swaps. */
  const activeMegaLabelRef = useRef<string | null>(null);
  const currentLocale = payload.languageSwitcher.locales.find(l => l.code === langCode) ?? payload.languageSwitcher.locales[0];
  const capsuleLabel = currentLocale ? formatLocaleCapsuleLabel(currentLocale) : langCode;
  const movePill = useCallback((key: string | null) => {
    const pill = pillRef.current;
    const nav = navRef.current;
    if (!(pill && nav && key)) {
      if (pill) {
        pill.style.opacity = "0";
      }
      return;
    }
    const btn = nav.querySelector(`[data-nav-key="${key}"]`);
    if (!btn) {
      return;
    }
    const nr = nav.getBoundingClientRect();
    const br = btn.getBoundingClientRect();
    pill.style.left = `${br.left - nr.left}px`;
    pill.style.width = `${br.width}px`;
    pill.style.opacity = "1";
  }, []);
  const highlightNav = useCallback((key_0: string) => {
    if (isCompact) {
      return;
    }
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    const root = payload.roots.find(r => r.id === key_0);
    if (root?.itemKind === "mega_tab") {
      activeMegaLabelRef.current = root.label.trim() || null;
    } else {
      activeMegaLabelRef.current = null;
    }
    setActiveTab(key_0);
    movePill(key_0);
  }, [isCompact, movePill, payload.roots]);
  const closeMenu = useCallback(() => {
    if (isCompact) {
      return;
    }
    closeTimer.current = setTimeout(() => {
      activeMegaLabelRef.current = null;
      setActiveTab(null);
      movePill(null);
    }, payload.closeDelayMs);
  }, [isCompact, movePill, payload.closeDelayMs]);
  const cancelClose = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);
  const openLangMenu = useCallback(() => {
    if (langCloseTimer.current) {
      clearTimeout(langCloseTimer.current);
      langCloseTimer.current = null;
    }
    setLangOpen(true);
  }, []);
  const scheduleCloseLangMenu = useCallback(() => {
    if (langCloseTimer.current) {
      clearTimeout(langCloseTimer.current);
    }
    langCloseTimer.current = setTimeout(() => {
      setLangOpen(false);
      langCloseTimer.current = null;
    }, 220);
  }, []);
  const closeLangMenuNow = useCallback(() => {
    if (langCloseTimer.current) {
      clearTimeout(langCloseTimer.current);
      langCloseTimer.current = null;
    }
    setLangOpen(false);
  }, []);
  const selectLocale = useCallback((loc: (typeof payload.languageSwitcher.locales)[number]) => {
    if (isLocaleCurrentForPath(loc, currentPathname)) {
      return;
    }
    setLangCode(loc.code);
    closeLangMenuNow();
    const href = loc.href?.trim();
    if (!(href && !previewMode && href !== "#")) {
      return;
    }
    window.location.assign(href);
  }, [closeLangMenuNow, currentPathname, previewMode]);
  useEffect(() => {
    return () => {
      if (langCloseTimer.current) {
        clearTimeout(langCloseTimer.current);
      }
    };
  }, []);
  const closeMobileMenu = useCallback(() => {
    setMobileMenuOpen(false);
    setExpandedMegaTabId(null);
    closeLangMenuNow();
    menuToggleRef.current?.focus();
  }, [closeLangMenuNow]);
  const toggleMobileMenu = useCallback(() => {
    setMobileMenuOpen(open => {
      if (open) {
        setExpandedMegaTabId(null);
        closeLangMenuNow();
      }
      return !open;
    });
  }, [closeLangMenuNow]);
  const toggleMegaAccordion = useCallback((tabId: string) => {
    setExpandedMegaTabId(current => current === tabId ? null : tabId);
    setOpenedMegaTabIds(current_0 => current_0.has(tabId) ? current_0 : new Set(current_0).add(tabId));
  }, []);
  useEffect(() => {
    if (!isCompact) {
      setMobileMenuOpen(false);
      setExpandedMegaTabId(null);
    }
  }, [isCompact]);

  // Rematch open mega by label when audience menuId swaps, or restore after hub nav.
  useLayoutEffect(() => {
    setExpandedMegaTabId(null);
    const sessionLabel = consumeOpenMegaTabLabel();
    const label = (sessionLabel ?? activeMegaLabelRef.current)?.trim() || null;
    if (label) {
      const match = findMegaTabByLabel(payload.roots, label);
      if (match) {
        activeMegaLabelRef.current = match.label.trim() || null;
        setActiveTab(match.id);
        movePill(match.id);
        return;
      }
    }
    // Drop highlight when the previous tab id vanished after an audience swap.
    setActiveTab(prev => {
      if (!prev) {
        return null;
      }
      const stillThere = payload.roots.some(r_0 => r_0.id === prev);
      if (stillThere) {
        return prev;
      }
      activeMegaLabelRef.current = null;
      movePill(null);
      return null;
    });
  }, [payload.menuId, payload.roots, movePill]);
  useEffect(() => {
    if (!onActiveMegaTabChange) {
      return;
    }
    const root_0 = payload.roots.find(r_1 => r_1.itemKind === "mega_tab" && r_1.id === activeTab);
    if (root_0?.itemKind === "mega_tab") {
      onActiveMegaTabChange({
        id: root_0.id,
        label: root_0.label.trim()
      });
      return;
    }
    onActiveMegaTabChange(null);
  }, [activeTab, onActiveMegaTabChange, payload.roots]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") {
        return;
      }
      if (mobileMenuOpen) {
        closeMobileMenu();
        return;
      }
      activeMegaLabelRef.current = null;
      setActiveTab(null);
      closeLangMenuNow();
      movePill(null);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [closeLangMenuNow, closeMobileMenu, mobileMenuOpen, movePill]);
  useEffect(() => {
    if (!(mobileMenuOpen && isCompact)) {
      return;
    }
    const prev_0 = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev_0;
    };
  }, [mobileMenuOpen, isCompact]);
  useEffect(() => {
    if (!(mobileMenuOpen && isCompact)) {
      return;
    }
    const drawer = drawerRef.current;
    if (!drawer) {
      return;
    }
    const focusables = drawer.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])');
    if (focusables.length === 0) {
      return;
    }
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    first.focus();
    const onKeyDown = (e_0: KeyboardEvent) => {
      if (e_0.key !== "Tab") {
        return;
      }
      if (e_0.shiftKey) {
        if (document.activeElement === first) {
          e_0.preventDefault();
          last.focus();
        }
        return;
      }
      if (document.activeElement === last) {
        e_0.preventDefault();
        first.focus();
      }
    };
    drawer.addEventListener("keydown", onKeyDown);
    return () => drawer.removeEventListener("keydown", onKeyDown);
  }, [mobileMenuOpen, isCompact]);
  const activeRoot = payload.roots.find(r_2 => r_2.itemKind === "mega_tab" && r_2.id === activeTab);
  const promos = payload.promos ?? {};
  const footerRows = payload.footerRows ?? {};
  const activePromo = activeRoot && activeRoot.itemKind === "mega_tab" ? promos[activeRoot.id] : undefined;
  const activeFooter = activeRoot && activeRoot.itemKind === "mega_tab" ? footerRows[activeRoot.id] : undefined;
  const megaOpen = !isCompact && activeTab != null && activeRoot?.itemKind === "mega_tab";
  const navChromeOpen = !isCompact && activeTab != null;
  const logoHref = safeLogoHref(payload.logoHref, payload.logoUrl);
  const logoSrc = payload.logoUrl?.trim();
  const logoOpenSrc = payload.logoOpenUrl?.trim();
  const showOpenLogo = Boolean(navChromeOpen) && Boolean(logoOpenSrc) && logoOpenSrc !== logoSrc;
  const logoIntrinsic = imgElementIntrinsicProps(payload.logoWidth, payload.logoHeight);
  const logoWidth = logoIntrinsic.width ?? 180;
  const logoHeight = logoIntrinsic.height ?? 36;
  const rootRef = useRef<HTMLDivElement>(null);
  const stickyUi = useMegaMenuStickyHeader({
    enabled: payload.sticky === true,
    lockVisible: megaOpen || mobileMenuOpen || langOpen,
    mode: payload.stickyMode === "smart" ? "smart" : "always",
    previewMode,
    rootRef
  });

  /** Layout-critical: must be on first paint (SSR + hydration), not useEffect. */
  const panelContentMaxPx = resolvePanelContentMaxWidthPx(payload.design.panelContentMaxWidthPx);
  const logoDisplayHeightPx = resolveMegaMenuLogoDisplayHeightPx(payload.logoHeightPx, payload.logoSizeVariant);
  const logoRasterMaxPx = resolveMegaMenuLogoRasterMaxPx(logoDisplayHeightPx, payload.logoSizeVariant);
  const logoSizes = payload.logoSizes?.trim() || `(max-width: 767px) min(50vw, 140px), ${logoRasterMaxPx}px`;
  const rootLayoutStyle = {
    ["--mmh-col-max-width" as string]: `${resolveColumnMaxWidthPx(payload.design.columnMaxWidthPx)}px`,
    ...(panelContentMaxPx == null ? {} : {
      ["--mmh-panel-content-max" as string]: `${panelContentMaxPx}px`
    }),
    ...(logoDisplayHeightPx == null ? {} : {
      ["--mmh-logo-height" as string]: `${logoDisplayHeightPx}px`
    })
  };
  useEffect(() => {
    const root_1 = rootRef.current;
    if (!root_1) {
      return;
    }
    // Colors: cosmetic, fine after paint.
    applyMegaMenuDesignCssVars(root_1, payload.design);
  }, [payload.design.bubbleBackground, payload.design.pillColor, payload.design.textColor, payload.design.navActiveTextColor, payload.design.navInactiveOpenTextColor, payload.design.categoryColor, payload.design.categoryHoverColor, payload.design.linkColor, payload.design.linkDescriptionColor, payload.design.linkHoverColor, payload.design.linkHoverBackground, payload.design.footerLabelColor, payload.design.footerPillTextColor, payload.design.footerPillBackground, payload.design.footerPillBorderColor, payload.design.footerPillHoverBackground, payload.design.footerPillHoverTextColor, payload.design.ctaBackground, payload.design.ctaTextColor, payload.design.ctaHoverBackground, payload.design.audienceToggleBorderColor, payload.design.audienceToggleActiveBackground, payload.design.audienceToggleActiveTextColor, payload.design.audienceToggleInactiveBackground, payload.design.audienceToggleInactiveTextColor, payload.design.audienceToggleInactiveHoverBackground]);
  const renderLangOptions = (listboxId: string) => <div aria-label="Languages" className={styles.langDd} id={listboxId} role="listbox">
			{payload.languageSwitcher.locales.map(loc_0 => {
      const isCurrent = isLocaleCurrentForPath(loc_0, currentPathname);
      const isSelected = loc_0.code === langCode;
      return <div className={styles.langOptionWrap} key={loc_0.code}>
						<button {...isSelected ? {
          "aria-selected": "true"
        } : {
          "aria-selected": "false"
        }} aria-current={isCurrent ? "true" : undefined} aria-disabled={isCurrent ? "true" : undefined} className={`${styles.langItem} ${isCurrent ? styles.langItemCurrent : ""}`} disabled={isCurrent} onClick={() => selectLocale(loc_0)} role="option" type="button">
							<span aria-hidden className={styles.langItemFlag}>
								{loc_0.flag}
							</span>
							<span>{loc_0.label}</span>
							{isCurrent ? <span className={styles.langItemCurrentHint}>Current</span> : null}
						</button>
					</div>;
    })}
		</div>;
  const renderDrawerActions = (inFooter: boolean) => <div className={inFooter ? styles.drawerFooterActions : styles.drawerInlineActions}>
			{payload.languageSwitcher.enabled ? <div className={`${styles.langWrap} ${langOpen ? styles.langWrapOpen : ""}`}>
					<button {...langOpen ? ARIA_EXPANDED_TRUE : ARIA_EXPANDED_FALSE} aria-controls="mmh-lang-listbox-drawer" aria-haspopup="listbox" aria-label={`Language: ${capsuleLabel}`} className={styles.langBtn} onClick={() => setLangOpen(o => !o)} type="button">
						<LangGlobeIcon />
						<span className={styles.langBtnText}>{capsuleLabel}</span>
					</button>
					{renderLangOptions("mmh-lang-listbox-drawer")}
				</div> : null}
			{payload.contact.enabled ? <a className={styles.contact} href={payload.contact.href}>
					{payload.contact.label}
				</a> : null}
			{payload.cta.enabled ? <a className={styles.ctaWrap} href={payload.cta.href}>
					<span aria-hidden className={styles.ctaSwipe} />
					<span className={styles.ctaLabel}>{payload.cta.label}</span>
				</a> : null}
		</div>;
  const blockPreviewLinkNav = useCallback((e_1: ReactMouseEvent) => {
    if (!previewMode) {
      return;
    }
    const target = e_1.target;
    if (!(target instanceof Element)) {
      return;
    }
    const anchor = target.closest("a[href]");
    if (anchor) {
      e_1.preventDefault();
      e_1.stopPropagation();
    }
  }, [previewMode]);
  return <div className={[styles.root, payload.sticky === true ? styles.rootSticky : "", stickyUi.stuck ? styles.rootStuck : "", stickyUi.retracted ? styles.rootRetracted : ""].filter(Boolean).join(" ")} data-pb-mega-menu="" data-sticky={payload.sticky === true ? "true" : "false"} data-viewport={viewport} onClickCapture={previewMode ? blockPreviewLinkNav : undefined} onMouseLeave={isCompact ? undefined : closeMenu} ref={rootRef} style={rootLayoutStyle}>
			<div className={`${styles.bubble} ${navChromeOpen ? styles.bubbleOpen : ""} ${megaOpen ? styles.bubbleMegaOpen : ""}`}>
				<div className={styles.topbar}>
					<div className={styles.topbarInner}>
						<div className={styles.brandCluster}>
							<a className={styles.logo} href={logoHref}>
								{logoSrc ? <span className={styles.logoImgWrap}>
										<img alt={showOpenLogo && logoOpenSrc ? "" : payload.logoAlt || ""} aria-hidden={showOpenLogo ? true : undefined} className={`${styles.logoImg} ${showOpenLogo ? styles.logoImgFaded : ""}`} height={logoHeight} sizes={logoSizes} src={logoSrc} srcSet={payload.logoSrcSet ?? undefined} width={logoWidth} />
										{logoOpenSrc ? <img alt={payload.logoAlt || ""} aria-hidden={showOpenLogo ? undefined : true} className={`${styles.logoImg} ${styles.logoImgOverlay} ${showOpenLogo ? styles.logoImgOverlayVisible : ""}`} height={logoHeight} sizes={logoSizes} src={logoOpenSrc} width={logoWidth} /> : null}
									</span> : <span>{payload.logoAlt || "Logo"}</span>}
							</a>
							{afterLogo}
						</div>

						<nav aria-label="Primary" className={styles.navDesktop} ref={navRef}>
							<span aria-hidden className={styles.pill} ref={pillRef} />
							{payload.roots.map((root_2, rootIndex) => {
              const key_1 = menuRootKey(root_2, rootIndex);
              if (root_2.itemKind === "link") {
                const navKey = root_2.id || key_1;
                const isActive = activeTab === navKey;
                return <a className={`${styles.navBtn} ${isActive ? styles.navBtnActive : ""}`} data-nav-key={navKey} href={root_2.href} key={key_1} onFocus={() => highlightNav(navKey)} onMouseEnter={() => highlightNav(navKey)} rel={root_2.openInNewTab ? "noopener noreferrer" : undefined} target={root_2.openInNewTab ? "_blank" : undefined}>
											{root_2.label}
										</a>;
              }
              if (root_2.itemKind !== "mega_tab") {
                return null;
              }
              const isActive_0 = activeTab === root_2.id;
              const hasMegaPanel = megaTabHasPanelContent(root_2, promos, footerRows);
              return <button key={key_1} {...hasMegaPanel ? {
                ...(isActive_0 ? ARIA_EXPANDED_TRUE : ARIA_EXPANDED_FALSE),
                "aria-haspopup": "menu"
              } : {}} className={`${styles.navBtn} ${isActive_0 ? styles.navBtnActive : ""}`} data-nav-key={root_2.id} onFocus={() => highlightNav(root_2.id)} onMouseEnter={() => highlightNav(root_2.id)} type="button">
										<span className={styles.navBtnLabel}>{root_2.label}</span>
										{hasMegaPanel ? <span aria-hidden className={styles.navChevron}>
												▾
											</span> : null}
									</button>;
            })}
						</nav>

						<div className={styles.rightDesktop}>
							{payload.languageSwitcher.enabled ? <div className={`${styles.langWrap} ${langOpen ? styles.langWrapOpen : ""}`} onMouseEnter={openLangMenu} onMouseLeave={scheduleCloseLangMenu}>
									<button {...langOpen ? ARIA_EXPANDED_TRUE : ARIA_EXPANDED_FALSE} aria-controls="mmh-lang-listbox-desktop" aria-haspopup="listbox" aria-label={`Language: ${capsuleLabel}`} className={styles.langBtn} onFocus={openLangMenu} type="button">
										<LangGlobeIcon />
										<span className={styles.langBtnText}>{capsuleLabel}</span>
									</button>
									{renderLangOptions("mmh-lang-listbox-desktop")}
								</div> : null}
							{payload.contact.enabled ? <a className={styles.contact} href={payload.contact.href}>
									{payload.contact.label}
								</a> : null}
							{payload.cta.enabled ? <a className={styles.ctaWrap} href={payload.cta.href}>
									<span aria-hidden className={styles.ctaSwipe} />
									<span className={styles.ctaLabel}>{payload.cta.label}</span>
								</a> : null}
						</div>

						<button aria-controls="mmh-mobile-drawer" {...mobileMenuOpen ? ARIA_EXPANDED_TRUE : ARIA_EXPANDED_FALSE} aria-label={mobileMenuOpen ? "Close menu" : "Open menu"} className={styles.menuToggle} onClick={toggleMobileMenu} ref={menuToggleRef} type="button">
							<span className={`${styles.menuToggleBar} ${mobileMenuOpen ? styles.menuToggleBarOpen : ""}`} />
							<span className={`${styles.menuToggleBar} ${mobileMenuOpen ? styles.menuToggleBarOpenMid : ""}`} />
							<span className={`${styles.menuToggleBar} ${mobileMenuOpen ? styles.menuToggleBarOpenBot : ""}`} />
						</button>
					</div>
				</div>

				{megaOpen && activeRoot?.itemKind === "mega_tab" ? <div className={`${styles.mega} ${styles.megaDesktop} ${styles.megaVisible}`} onMouseEnter={cancelClose} onMouseLeave={closeMenu}>
						<MegaPanelColumns centerColumns={payload.design.centerColumns !== false} columnMaxWidthPx={payload.design.columnMaxWidthPx} footerRow={activeFooter} promo={activePromo} root={activeRoot} />
					</div> : null}
			</div>

			<button {...mobileMenuOpen ? ARIA_HIDDEN_FALSE : ARIA_HIDDEN_TRUE} aria-label="Close menu" className={`${styles.drawerBackdrop} ${mobileMenuOpen ? styles.drawerBackdropVisible : ""}`} onClick={closeMobileMenu} tabIndex={mobileMenuOpen ? 0 : -1} type="button" />

			<aside {...mobileMenuOpen ? ARIA_HIDDEN_FALSE : ARIA_HIDDEN_TRUE} className={`${styles.drawer} ${mobileMenuOpen ? styles.drawerOpen : ""}`} id="mmh-mobile-drawer" ref={drawerRef}>
				<div className={styles.drawerHeader}>
					<span className={styles.drawerTitle}>Menu</span>
					<button aria-label="Close menu" className={styles.drawerClose} onClick={closeMobileMenu} type="button">
						×
					</button>
				</div>

				{drawerLead ? <div className={styles.drawerLead}>{drawerLead}</div> : null}

				<nav aria-label="Mobile primary" className={styles.drawerNav}>
					<ul className={styles.drawerNavList}>
						{payload.roots.map((root_3, rootIndex_0) => {
            const key_2 = menuRootKey(root_3, rootIndex_0);
            if (root_3.itemKind === "link") {
              return <li className={styles.drawerNavItem} key={key_2}>
										<a className={styles.drawerLink} href={root_3.href} onClick={closeMobileMenu} rel={root_3.openInNewTab ? "noopener noreferrer" : undefined} target={root_3.openInNewTab ? "_blank" : undefined}>
											{root_3.label}
										</a>
									</li>;
            }
            if (root_3.itemKind !== "mega_tab") {
              return null;
            }
            const expanded = expandedMegaTabId === root_3.id;
            const tabPromo = promos[root_3.id];
            const tabFooter = footerRows[root_3.id];
            return <li className={styles.drawerNavItem} key={key_2}>
									<button aria-controls={`mmh-accordion-${root_3.id}`} {...expanded ? ARIA_EXPANDED_TRUE : ARIA_EXPANDED_FALSE} className={styles.drawerAccordionBtn} onClick={() => toggleMegaAccordion(root_3.id)} type="button">
										<span>{root_3.label}</span>
										<span aria-hidden className={`${styles.drawerChevron} ${expanded ? styles.drawerChevronOpen : ""}`}>
											▾
										</span>
									</button>
									<div className={`${styles.drawerAccordionPanel} ${expanded ? styles.drawerAccordionPanelOpen : ""}`} hidden={!expanded} id={`mmh-accordion-${root_3.id}`}>
										{openedMegaTabIds.has(root_3.id) ? <MegaPanelColumns centerColumns={payload.design.centerColumns !== false} columnClassName={styles.drawerCol} columnMaxWidthPx={payload.design.columnMaxWidthPx} footerRow={tabFooter} gridClassName={viewport === "tablet" ? styles.drawerMegaGridTablet : styles.drawerMegaGridMobile} promo={tabPromo} root={root_3} /> : null}
									</div>
								</li>;
          })}
					</ul>
				</nav>

				<footer className={styles.drawerFooter}>
					{renderDrawerActions(true)}
				</footer>
			</aside>
		</div>;
}